"use client";

import { useEffect, useRef } from "react";
import { useAppSelector } from "@/app/store/hook";
import {
  handleLogRocketLogout,
  identifyLogRocketUser,
  initLogRocket,
  trackLogRocketEvent,
} from "@/app/lib/logrocket";

/**
 * Boots LogRocket on the client and keeps the identified user in sync with the
 * Redux auth state. Mounted once from `app/provider.tsx`.
 *
 * Everything here is a no-op when `NEXT_PUBLIC_LOGROCKET_APP_ID` is missing, so
 * the app behaves exactly as before until the key is configured.
 */
export function LogRocketProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const currentUser = useAppSelector((state) => state.user.currentUser);
  const identifiedUserId = useRef<string | null>(null);

  // 1️⃣ Initialise as early as possible on the client (idempotent + SSR safe).
  useEffect(() => {
    initLogRocket();
  }, []);

  // 2️⃣ Identify on login / refresh, start a clean session on logout.
  useEffect(() => {
    if (currentUser?._id) {
      if (identifiedUserId.current === currentUser._id) return;

      identifyLogRocketUser(currentUser);
      identifiedUserId.current = currentUser._id;
      trackLogRocketEvent("User Identified", {
        isAdmin: Boolean(currentUser.isAdmin),
      });
      return;
    }

    // User just logged out (or session expired)
    if (identifiedUserId.current) {
      handleLogRocketLogout();
      identifiedUserId.current = null;
    }
  }, [currentUser]);

  return <>{children}</>;
}
